import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/useEntity';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input, Textarea } from '../ui/Input';
import { Select } from '../ui/Select';
import { toast } from '../../lib/toast';
import { ACCION_LABEL, ACCIONES_COMERCIALES } from '../../../shared/rotacion';

/**
 * Edición de los datos maestros del producto en un modal (ficha nueva,
 * rediseño UX fase 3). Misma lógica de guardado que tenía la ficha clásica
 * en línea: PUT /productos/:id con los campos, sustituto por SKU en su
 * propio endpoint y enlace de la ficha de proveedor si cambia el proveedor.
 */

export type ProductoEditable = {
  id: string;
  sku: string;
  ean: string | null;
  nombre: string;
  proveedor_id: string | null;
  ref_proveedor: string | null;
  part_number: string | null;
  coste: number | null;
  pvpr: number | null;
  precio_target: number | null;
  margen_pct: number | null;
  revision_pendiente: boolean;
  /** Excluido del descuento técnico de su cliente (migración 20261026_1200). */
  sin_dto_tecnico?: boolean;
  estado: string;
  accion_comercial: string;
  stock_minimo: number | null;
  notas: string | null;
  familia: string | null;
  fecha_lanzamiento: string | null;
  ficha_descripcion: string | null;
  gama_id: string | null;
  categoria_id: string | null;
  subcategoria_id: string | null;
  detalle_id: string | null;
  url_oficial: string | null;
  sustituido_por_sku: string | null;
  cliente_codigo: string | null;
  cliente_id: string | null;
  marca_id: string | null;
  marca_nombre: string | null;
  categoria_nombre: string | null;
  subcategoria_nombre: string | null;
};

const ESTADOS_CON_SUSTITUTO = ['DESCATALOGADO', 'BUSCAR_SUSTITUTO'];
const IVA_GENERAL_PCT = 21;

/** Margen del cliente: PVPr con IVA → sin IVA; (PVPr − coste) / PVPr. */
export function calcMargenCliente(coste: number | null, pvpr: number | null): number | null {
  if (coste == null || pvpr == null) return null;
  const c = Number(coste);
  const pConIva = Number(pvpr);
  if (!Number.isFinite(c) || !Number.isFinite(pConIva) || pConIva <= 0) return null;
  const p = pConIva / (1 + IVA_GENERAL_PCT / 100);
  if (p <= 0) return null;
  return Number((((p - c) / p) * 100).toFixed(2));
}

type Draft = {
  sku: string; ean: string; nombre: string; proveedor_id: string; ref_proveedor: string; part_number: string;
  coste: number | null; pvpr: number | null; precio_target: number | null; margen_pct: number | null;
  revision_pendiente: boolean; sin_dto_tecnico: boolean; estado: string; accion_comercial: string; stock_minimo: number | null; notas: string; familia: string; fecha_lanzamiento: string;
  ficha_descripcion: string; marca_id: string; gama_id: string; categoria_id: string; subcategoria_id: string; detalle_id: string; url_oficial: string; sustituido_por_sku: string;
};

export function ProductoEditarModal({ producto: p, open, onClose, onSaved }: { producto: ProductoEditable; open: boolean; onClose: () => void; onSaved: () => void }) {
  const qc = useQueryClient();
  const [draft, setDraft] = useState<Draft>(() => desdeProducto(p));
  // El borrador se carga al ABRIR el modal, no en cada refetch del producto
  // (un refetch en segundo plano no debe pisar lo que se está tecleando).
  const abiertoAntes = useRef(false);
  useEffect(() => {
    if (open && !abiertoAntes.current) setDraft(desdeProducto(p));
    abiertoAntes.current = open;
  }, [open, p]);

  const estados = useQuery({ queryKey: ['list', '/admin/producto-estados'], queryFn: () => api.get<{ data: { codigo: string; nombre: string; color: string }[] }>('/admin/producto-estados'), enabled: open });
  const gamas = useQuery({ queryKey: ['list', '/admin/gamas'], queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/admin/gamas'), enabled: open });
  const categorias = useQuery({ queryKey: ['list', '/admin/categorias'], queryFn: () => api.get<{ data: { id: string; nombre: string; activo: boolean }[] }>('/admin/categorias?limit=1000'), enabled: open });
  const subcategorias = useQuery({ queryKey: ['list', '/admin/subcategorias'], queryFn: () => api.get<{ data: { id: string; nombre: string; categoria_id: string; activo: boolean }[] }>('/admin/subcategorias?limit=1000'), enabled: open });
  const detalles = useQuery({ queryKey: ['list', '/admin/detalles'], queryFn: () => api.get<{ data: { id: string; nombre: string; subcategoria_id: string; activo: boolean }[] }>('/admin/detalles?limit=1000'), enabled: open });
  const marcas = useQuery({ queryKey: ['list', '/admin/marcas'], queryFn: () => api.get<{ data: { id: string; nombre: string; cliente_id: string }[] }>('/admin/marcas'), enabled: open });
  const proveedores = useQuery({ queryKey: ['list', '/proveedores'], queryFn: () => api.get<{ data: { id: string; nombre: string }[] }>('/proveedores'), enabled: open });

  // Solo marcas del mismo cliente: la marca es la que fija el cliente del
  // producto, y cambiarla a la de otro lo sacaría de su catálogo.
  const marcasOpc = useMemo(
    () => (marcas.data?.data ?? []).filter((m) => !p.cliente_id || m.cliente_id === p.cliente_id).map((m) => ({ value: m.id, label: m.nombre })),
    [marcas.data, p.cliente_id],
  );

  // Cascada categoría › subcategoría › detalle: cada nivel ofrece solo los
  // hijos del nivel de arriba (los inactivos solo si son el valor actual).
  const categoriasOpc = useMemo(
    () => (categorias.data?.data ?? []).filter((c) => c.activo !== false || c.id === p.categoria_id).map((c) => ({ value: c.id, label: c.nombre })),
    [categorias.data, p.categoria_id],
  );
  const subcategoriasOpc = useMemo(
    () => (subcategorias.data?.data ?? []).filter((x) => x.categoria_id === draft.categoria_id && (x.activo !== false || x.id === p.subcategoria_id)).map((x) => ({ value: x.id, label: x.nombre })),
    [subcategorias.data, draft.categoria_id, p.subcategoria_id],
  );
  const detallesOpc = useMemo(
    () => (detalles.data?.data ?? []).filter((d) => d.subcategoria_id === draft.subcategoria_id && (d.activo || d.id === p.detalle_id)).map((d) => ({ value: d.id, label: d.nombre })),
    [detalles.data, draft.subcategoria_id, p.detalle_id],
  );
  const cambiarCategoria = (id: string) => setDraft((d) => {
    const subSigue = (subcategorias.data?.data ?? []).some((x) => x.id === d.subcategoria_id && x.categoria_id === id);
    return subSigue ? { ...d, categoria_id: id } : { ...d, categoria_id: id, subcategoria_id: '', detalle_id: '' };
  });
  const cambiarSubcategoria = (id: string) => setDraft((d) => {
    const detSigue = (detalles.data?.data ?? []).some((x) => x.id === d.detalle_id && x.subcategoria_id === id);
    return detSigue ? { ...d, subcategoria_id: id } : { ...d, subcategoria_id: id, detalle_id: '' };
  });

  // Margen autocalculado al cambiar coste o PVPr.
  useEffect(() => {
    const auto = calcMargenCliente(draft.coste, draft.pvpr);
    setDraft((d) => (d.margen_pct === auto ? d : { ...d, margen_pct: auto }));
  }, [draft.coste, draft.pvpr]);

  const guardar = useMutation({
    mutationFn: async () => {
      const body: Record<string, unknown> = {};
      for (const [key, value] of Object.entries(draft)) {
        if (key === 'sustituido_por_sku') continue;
        if (key === 'marca_id' && !value) continue; // marca obligatoria: nunca se vacía
        if (value === '') body[key] = null;
        else if (key === 'margen_pct' && typeof value === 'number' && !Number.isFinite(value)) continue;
        else body[key] = value;
      }
      await api.put(`/productos/${p.id}`, body);
      const avisos: string[] = [];
      const sustAntes = (p.sustituido_por_sku ?? '').toUpperCase();
      const sustAhora = draft.sustituido_por_sku.trim().toUpperCase();
      if (sustAhora !== sustAntes) {
        try { await api.put(`/productos/${p.id}/sustituto`, { sku: draft.sustituido_por_sku.trim() || null }); }
        catch (err) { avisos.push(`No se pudo fijar el sustituto: ${(err as Error).message}`); }
      }
      if (draft.proveedor_id !== (p.proveedor_id ?? '')) {
        await api.post(`/productos/${p.id}/vincular-proveedor`, { proveedor_id: draft.proveedor_id || null, ref_proveedor: draft.ref_proveedor.trim() || undefined });
      }
      return avisos;
    },
    onSuccess: (avisos) => {
      qc.invalidateQueries({ queryKey: ['producto', p.id] });
      qc.invalidateQueries({ queryKey: ['productos', 'listado-full'] });
      qc.invalidateQueries({ queryKey: ['catalogo'] });
      if (avisos.length) toast.warning('Guardado con avisos', avisos.join('\n')); else toast.success('Producto guardado');
      onSaved();
    },
    onError: (err) => toast.error('No se pudo guardar el producto', (err as Error).message),
  });

  const set = <K extends keyof Draft>(k: K, v: Draft[K]) => setDraft((d) => ({ ...d, [k]: v }));
  const num = (v: string) => (v === '' ? null : Number(v));
  const esGame = p.cliente_codigo === 'GAME';

  return (
    <Modal open={open} onClose={onClose} title={`Editar ${p.sku}`} size="xl" closeOnBackdrop={false} closeOnEsc={false}
      footer={<>
        <Button variant="secondary" onClick={onClose} disabled={guardar.isPending}>Cancelar</Button>
        <Button variant="save" onClick={() => guardar.mutate()} disabled={guardar.isPending}>{guardar.isPending ? 'Guardando…' : 'Guardar'}</Button>
      </>}
    >
      <div className="grid grid-cols-1 gap-x-5 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
        <Campo label="Nombre" className="sm:col-span-2 lg:col-span-3"><Input value={draft.nombre} onChange={(e) => set('nombre', e.target.value)} /></Campo>
        <Campo label="SKU"><Input className="font-mono" value={draft.sku} onChange={(e) => set('sku', e.target.value)} /></Campo>
        <Campo label="EAN"><Input className="font-mono" value={draft.ean} onChange={(e) => set('ean', e.target.value)} /></Campo>
        <Campo label="Part number" hint="Referencia del fabricante."><Input className="font-mono" value={draft.part_number} onChange={(e) => set('part_number', e.target.value)} /></Campo>
        <Campo label="Proveedor" hint="Al asignarlo se crea o enlaza su ficha en Proveedores.">
          <Select value={draft.proveedor_id} onChange={(e) => set('proveedor_id', e.target.value)} options={(proveedores.data?.data ?? []).map((x) => ({ value: x.id, label: x.nombre }))} />
        </Campo>
        <Campo label="Ref. proveedor"><Input className="font-mono" value={draft.ref_proveedor} onChange={(e) => set('ref_proveedor', e.target.value)} /></Campo>
        <Campo label="Estado">
          <Select value={draft.estado} emptyLabel={null} onChange={(e) => set('estado', e.target.value)} options={(estados.data?.data ?? []).map((x) => ({ value: x.codigo, label: x.nombre }))} />
        </Campo>
        <Campo label="Acción / estrategia comercial" hint={draft.estado === 'DESCATALOGADO' ? 'Al descatalogar pasa a Liquidar.' : undefined}>
          <Select value={draft.accion_comercial} emptyLabel={null} onChange={(e) => set('accion_comercial', e.target.value)} options={ACCIONES_COMERCIALES.map((a) => ({ value: a, label: ACCION_LABEL[a] }))} />
        </Campo>
        <Campo label="Stock mínimo" hint="Vacío = el de su condición comercial (subtipo › tipo › marca) o el global de Parámetros.">
          <Input type="number" min={0} step={1} value={draft.stock_minimo ?? ''} onChange={(e) => set('stock_minimo', e.target.value === '' ? null : Math.max(0, Math.round(Number(e.target.value))))} />
        </Campo>
        <Campo label="Marca" hint="Fija la condición comercial y la comisión.">
          <Select value={draft.marca_id} emptyLabel={null} onChange={(e) => set('marca_id', e.target.value)} options={marcasOpc.length ? marcasOpc : [{ value: draft.marca_id, label: p.marca_nombre ?? '—' }]} />
        </Campo>
        <Campo label="Gama">
          <Select value={draft.gama_id} onChange={(e) => set('gama_id', e.target.value)} options={(gamas.data?.data ?? []).map((x) => ({ value: x.id, label: x.nombre }))} />
        </Campo>
        <Campo label="Categoría">
          <Select value={draft.categoria_id} onChange={(e) => cambiarCategoria(e.target.value)} options={categoriasOpc.length ? categoriasOpc : draft.categoria_id ? [{ value: draft.categoria_id, label: p.categoria_nombre ?? '—' }] : []} />
        </Campo>
        <Campo label="Subcategoría" hint={draft.categoria_id ? undefined : 'Elige antes la categoría.'}>
          <Select value={draft.subcategoria_id} onChange={(e) => cambiarSubcategoria(e.target.value)} disabled={!draft.categoria_id} options={subcategoriasOpc.length ? subcategoriasOpc : draft.subcategoria_id ? [{ value: draft.subcategoria_id, label: p.subcategoria_nombre ?? '—' }] : []} />
        </Campo>
        <Campo label="Detalle" hint="Cuarta capa: cuelga de la subcategoría."><Select value={draft.detalle_id} onChange={(e) => set('detalle_id', e.target.value)} options={detallesOpc} /></Campo>
        <Campo label="Coste (EUR)"><Input type="number" step="any" value={draft.coste ?? ''} onChange={(e) => set('coste', num(e.target.value))} /></Campo>
        <Campo label="PVPr (EUR, con IVA)"><Input type="number" step="any" value={draft.pvpr ?? ''} onChange={(e) => set('pvpr', num(e.target.value))} /></Campo>
        <Campo label="Margen del cliente" hint="Autocalculado sobre el PVPr sin IVA."><div className="py-2 text-sm font-semibold text-slate-700">{draft.margen_pct != null ? `${draft.margen_pct.toFixed(2).replace('.', ',')} %` : '—'}</div></Campo>
        <Campo label="Target (EUR)" hint="Precio objetivo, solo informativo."><Input type="number" step="any" value={draft.precio_target ?? ''} onChange={(e) => set('precio_target', num(e.target.value))} /></Campo>
        <Campo label="URL oficial" hint={esGame ? 'Para GAME: https://www.game.es/<sku>' : 'Ficha oficial en la web del cliente.'} className="sm:col-span-2"><Input value={draft.url_oficial} onChange={(e) => set('url_oficial', e.target.value)} placeholder="https://…" /></Campo>
        {ESTADOS_CON_SUSTITUTO.includes(draft.estado) && (
          <Campo label="Sustituido por (SKU)" hint="Del mismo cliente. Vacío = sin sustituto."><Input className="font-mono" value={draft.sustituido_por_sku} onChange={(e) => set('sustituido_por_sku', e.target.value)} /></Campo>
        )}
        <Campo label="Familia"><Input value={draft.familia} onChange={(e) => set('familia', e.target.value)} /></Campo>
        <Campo label="Fecha de lanzamiento"><Input type="date" value={draft.fecha_lanzamiento} onChange={(e) => set('fecha_lanzamiento', e.target.value)} /></Campo>
        <Campo label="Revisión pendiente"><label className="flex cursor-pointer items-center gap-2 py-2 text-sm"><input type="checkbox" checked={draft.revision_pendiente} onChange={(e) => set('revision_pendiente', e.target.checked)} /> Marcar para revisar la ficha</label></Campo>
        <Campo label="Descuento técnico" hint="Para productos que el cliente no descuenta en factura aunque su categoría sí (p. ej. fuentes externas de portátil). Monitores y fuentes de componentes ya van excluidos.">
          <label className="flex cursor-pointer items-center gap-2 py-2 text-sm"><input type="checkbox" checked={draft.sin_dto_tecnico} onChange={(e) => set('sin_dto_tecnico', e.target.checked)} /> Sin descuento técnico</label>
        </Campo>
        <Campo label="Notas internas" className="sm:col-span-2 lg:col-span-3"><Textarea rows={3} value={draft.notas} onChange={(e) => set('notas', e.target.value)} /></Campo>
        <Campo label="Ficha comercial" hint="Características que se comparten con clientes." className="sm:col-span-2 lg:col-span-3"><Textarea rows={5} value={draft.ficha_descripcion} onChange={(e) => set('ficha_descripcion', e.target.value)} /></Campo>
      </div>
    </Modal>
  );
}

function desdeProducto(p: ProductoEditable): Draft {
  return {
    sku: p.sku, ean: p.ean ?? '', nombre: p.nombre, proveedor_id: p.proveedor_id ?? '', ref_proveedor: p.ref_proveedor ?? '', part_number: p.part_number ?? '',
    coste: p.coste != null ? Number(p.coste) : null, pvpr: p.pvpr != null ? Number(p.pvpr) : null, precio_target: p.precio_target != null ? Number(p.precio_target) : null,
    margen_pct: p.margen_pct != null ? Number(p.margen_pct) : null, revision_pendiente: p.revision_pendiente, sin_dto_tecnico: !!p.sin_dto_tecnico, estado: p.estado,
    accion_comercial: p.accion_comercial ?? 'MANTENER', stock_minimo: p.stock_minimo != null ? Number(p.stock_minimo) : null, notas: p.notas ?? '', familia: p.familia ?? '',
    fecha_lanzamiento: p.fecha_lanzamiento ? p.fecha_lanzamiento.slice(0, 10) : '', ficha_descripcion: p.ficha_descripcion ?? '', marca_id: p.marca_id ?? '', gama_id: p.gama_id ?? '', categoria_id: p.categoria_id ?? '', subcategoria_id: p.subcategoria_id ?? '', detalle_id: p.detalle_id ?? '',
    url_oficial: p.url_oficial ?? '', sustituido_por_sku: p.sustituido_por_sku ?? '',
  };
}

function Campo({ label, hint, className = '', children }: { label: string; hint?: string; className?: string; children: React.ReactNode }) {
  return (
    <div className={className}>
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500" title={hint}>{label}</div>
      <div className="mt-0.5">{children}</div>
      {hint && <div className="mt-0.5 text-[11px] text-slate-400">{hint}</div>}
    </div>
  );
}
